import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { EditorState } from '@codemirror/state';
import {
  EditorView,
  keymap,
  placeholder as cmPlaceholder,
} from '@codemirror/view';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from '@codemirror/commands';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { markdownLivePreview } from './markdownLivePreview';

export type MarkdownEditorHandle = {
  /** Insert at the current selection and move the cursor after the snippet. */
  insertAtCursor: (snippet: string) => void;
  focus: () => void;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  className?: string;
  /** Native-event handlers (CodeMirror dispatches them ahead of its own
   *  paste/drop handling; preventDefault() stops the editor's default). */
  onPaste?: (event: ClipboardEvent) => void;
  onDrop?: (event: DragEvent) => void;
  onDragOver?: (event: DragEvent) => void;
};

// Matches the `.input` recipe and the Dispatch Desk palette (light-only).
const editorTheme = EditorView.theme({
  '&': {
    fontSize: '0.875rem',
    color: '#212326',
  },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': {
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    lineHeight: '1.65',
  },
  '.cm-content': {
    padding: '12px 14px',
    minHeight: '400px',
    caretColor: '#212326',
  },
  '.cm-placeholder': { color: '#8B8E91' },
});

/**
 * Obsidian-style markdown editor: a single CodeMirror pane where formatting
 * renders live and syntax marks hide unless the cursor is on them.
 */
export const MarkdownEditor = forwardRef<MarkdownEditorHandle, Props>(
  function MarkdownEditor(
    {
      value,
      onChange,
      ariaLabel,
      placeholder,
      className = '',
      onPaste,
      onDrop,
      onDragOver,
    },
    ref,
  ) {
    const hostRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<EditorView | null>(null);

    // Latest-value refs so the view can be created exactly once.
    const valueRef = useRef(value);
    valueRef.current = value;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onPasteRef = useRef(onPaste);
    onPasteRef.current = onPaste;
    const onDropRef = useRef(onDrop);
    onDropRef.current = onDrop;
    const onDragOverRef = useRef(onDragOver);
    onDragOverRef.current = onDragOver;

    useEffect(() => {
      const view = new EditorView({
        parent: hostRef.current!,
        state: EditorState.create({
          doc: valueRef.current,
          extensions: [
            history(),
            // Tab indents (the editor is a focus trap while writing); Escape
            // blurs so keyboard users can still leave. defaultKeymap's Escape
            // (simplifySelection) runs first, so a multi-cursor collapses on
            // the first press and the second press exits.
            keymap.of([
              ...defaultKeymap,
              ...historyKeymap,
              indentWithTab,
              {
                key: 'Escape',
                run: (view) => {
                  view.contentDOM.blur();
                  return true;
                },
              },
            ]),
            markdown({ base: markdownLanguage }),
            markdownLivePreview(),
            EditorView.lineWrapping,
            ...(placeholder ? [cmPlaceholder(placeholder)] : []),
            // CodeMirror defaults autocorrect/autocapitalize to "off"; mobile
            // keyboards honour these, so opt back in — this field is prose.
            EditorView.contentAttributes.of({
              'aria-label': ariaLabel,
              spellcheck: 'true',
              autocorrect: 'on',
              autocapitalize: 'sentences',
            }),
            // These run before CodeMirror's own paste/drop handling; when the
            // image-upload hook claims the event (preventDefault), returning
            // true stops the editor from also inserting the file as text.
            EditorView.domEventHandlers({
              paste: (e) => {
                onPasteRef.current?.(e);
                return e.defaultPrevented;
              },
              drop: (e) => {
                onDropRef.current?.(e);
                return e.defaultPrevented;
              },
              dragover: (e) => {
                onDragOverRef.current?.(e);
                return e.defaultPrevented;
              },
            }),
            EditorView.updateListener.of((update) => {
              if (update.docChanged) {
                onChangeRef.current(update.state.doc.toString());
              }
            }),
            editorTheme,
          ],
        }),
      });
      viewRef.current = view;
      return () => {
        view.destroy();
        viewRef.current = null;
      };
    }, [ariaLabel, placeholder]);

    // Controlled-value sync (e.g. edit mode seeding the brief after load).
    useEffect(() => {
      const view = viewRef.current;
      if (!view) return;
      const current = view.state.doc.toString();
      if (value !== current) {
        view.dispatch({
          changes: { from: 0, to: current.length, insert: value },
        });
      }
    }, [value]);

    useImperativeHandle(
      ref,
      () => ({
        insertAtCursor(snippet: string) {
          const view = viewRef.current;
          if (!view) return;
          const { from, to } = view.state.selection.main;
          view.dispatch({
            changes: { from, to, insert: snippet },
            selection: { anchor: from + snippet.length },
            scrollIntoView: true,
          });
          view.focus();
        },
        focus() {
          viewRef.current?.focus();
        },
      }),
      [],
    );

    return (
      <div
        ref={hostRef}
        className={`w-full rounded-[5px] border border-line-strong bg-surface-1 text-ink focus-within:border-signal focus-within:ring-1 focus-within:ring-signal ${className}`}
      />
    );
  },
);
